import { getMagnifyingGlassIcon, getMicrophoneIcon } from '@utils/icons';
import i18n from '@i18n';

// AexSearchForm.js
interface AexSearchFormOptions {
	target: string | HTMLElement; // Selector or DOM element where the form will be appended
	position: 'before' | 'after' | 'replace' | 'append' | 'prepend'; // Position of the response in the target container (default is replace)
	index: string; // Index of the instance for unique class names
	placeholder?: string; // Placeholder text for the search input
	voiceSearch?: boolean; // Whether to include the voice button
}

/**
 * Creates and appends a search form to the target container.
 *
 * @param options - Configuration options for the search form
 * @returns The created form element
 */
export function AexSearchForm({
	target,
	position = 'replace',
	index,
	placeholder = i18n.t('searchPlaceholder'),
	voiceSearch = false,
}: AexSearchFormOptions): HTMLFormElement {
	const form = document.createElement('form');
	form.className = 'aex-search-form';
	form.classList.add('aex-search-form--injected');
	form.setAttribute('id', 'aex-search-form-' + index);
	form.setAttribute('aria-label', 'Search form');

	const appendGroup = document.createElement('div');
	appendGroup.className = 'aex-search-form-group';

	const input = document.createElement('input');
	input.type = 'search';
	input.placeholder = placeholder;
	input.setAttribute('maxLength', '255');
	input.setAttribute('aria-label', 'Search');
	appendGroup.appendChild(input);

	if (voiceSearch) {
		const voiceInputButton = document.createElement('button');
		voiceInputButton.type = 'button';
		voiceInputButton.className = 'aex-voice-button';
		voiceInputButton.innerHTML = getMicrophoneIcon();
		appendGroup.appendChild(voiceInputButton);
	}

	const submitButton = document.createElement('button');
	submitButton.type = 'submit';
	submitButton.className = 'search-button submit-button';
	submitButton.setAttribute('aria-label', 'Search');
	submitButton.innerHTML = getMagnifyingGlassIcon();
	appendGroup.appendChild(submitButton);

	form.appendChild(appendGroup);

	const targetElement = typeof target === 'string' ? document.querySelector(target) : target;

	if (targetElement) {
		switch (position) {
		case 'before':
			targetElement.insertAdjacentElement('beforebegin', form);
			break;
		case 'after':
			targetElement.insertAdjacentElement('afterend', form);
			break;
		case 'prepend':
			targetElement.insertAdjacentElement('afterbegin', form);
			break;
		case 'append':
			targetElement.appendChild(form);
			break;
		case 'replace':
		default:
			targetElement.innerHTML = '';
			targetElement.append(form);
			break;
		}
	}

	return form;
}
